import React, {useEffect, useMemo, useState} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {colors} from "nxtconstants";
import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateSwp,
  DEFAULT_INPUT,
  formatCompactCurrency,
  INPUT_LIMITS,
  snapToStep
} from "./CalculatorLogic";
import {
  ChartHoverInfo,
  ChartLabel,
  MobileTabToggle,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {
  CalculatorSection,
  CalculatorWrapper,
  ChartContainer,
  ControlPanel,
  PageTitle,
  ResultsPanel,
  SWP_ACCENT
} from "./CalculatorStyle";

const SwpCalculator = () => {
  const [currentCorpus, setCurrentCorpus] = useState(DEFAULT_INPUT.currentCorpus);
  const [currentAge, setCurrentAge] = useState(DEFAULT_INPUT.currentAge);
  const [returnBeforeWithdrawal, setReturnBeforeWithdrawal] = useState(
    DEFAULT_INPUT.returnBeforeWithdrawal
  );
  const [withdrawalStartAge, setWithdrawalStartAge] = useState(
    DEFAULT_INPUT.withdrawalStartAge
  );
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(
    DEFAULT_INPUT.monthlyWithdrawal
  );
  const [yearlyWithdrawalIncrease, setYearlyWithdrawalIncrease] = useState(
    DEFAULT_INPUT.yearlyWithdrawalIncrease
  );
  const [returnDuringWithdrawal, setReturnDuringWithdrawal] = useState(
    DEFAULT_INPUT.returnDuringWithdrawal
  );
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  const handleCurrentAgeChange = (value) => {
    setCurrentAge(value);
  };

  const handleWithdrawalStartAgeChange = (value) => {
    setWithdrawalStartAge(
      Math.min(
        Math.max(value, INPUT_LIMITS.withdrawalStartAge.min),
        INPUT_LIMITS.withdrawalStartAge.max
      )
    );
  };

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateSwp({
        currentCorpus,
        currentAge,
        returnBeforeWithdrawal,
        withdrawalStartAge,
        monthlyWithdrawal,
        yearlyWithdrawalIncrease,
        returnDuringWithdrawal
      }),
    [
      currentCorpus,
      currentAge,
      returnBeforeWithdrawal,
      withdrawalStartAge,
      monthlyWithdrawal,
      yearlyWithdrawalIncrease,
      returnDuringWithdrawal
    ]
  );

  const chartData = useMemo(
    () =>
      result.returnData.map((row) => ({
        label: row.yearLabel,
        Investment: row.Investment,
        Gain: row.Gain
      })),
    [result.returnData]
  );

  const sliderProps = {
    accentColor: colors.primary
  };

  return (
    <CalculatorSection>
      <PageTitle>SWP Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <RangeSlider
            label="Current Corpus"
            value={currentCorpus}
            onChange={(value) =>
              setCurrentCorpus(snapToStep(value, INPUT_LIMITS.currentCorpus.step))
            }
            min={INPUT_LIMITS.currentCorpus.min}
            max={INPUT_LIMITS.currentCorpus.max}
            step={INPUT_LIMITS.currentCorpus.step}
            prefix="₹"
            {...sliderProps}
          />

          <RangeSlider
            label="Current Age"
            value={currentAge}
            onChange={handleCurrentAgeChange}
            min={INPUT_LIMITS.currentAge.min}
            max={INPUT_LIMITS.currentAge.max}
            step={INPUT_LIMITS.currentAge.step}
            suffix=" Years"
            {...sliderProps}
          />

          <RangeSlider
            label="Expected Return Before Withdrawal Phase"
            value={returnBeforeWithdrawal}
            onChange={setReturnBeforeWithdrawal}
            min={INPUT_LIMITS.returnBeforeWithdrawal.min}
            max={INPUT_LIMITS.returnBeforeWithdrawal.max}
            step={INPUT_LIMITS.returnBeforeWithdrawal.step}
            suffix="%"
            {...sliderProps}
          />

          <RangeSlider
            label="Withdrawal Start Age"
            value={withdrawalStartAge}
            onChange={handleWithdrawalStartAgeChange}
            min={INPUT_LIMITS.withdrawalStartAge.min}
            max={INPUT_LIMITS.withdrawalStartAge.max}
            step={INPUT_LIMITS.withdrawalStartAge.step}
            suffix=" Years"
            {...sliderProps}
          />

          <RangeSlider
            label="Monthly Withdrawal Amount"
            value={monthlyWithdrawal}
            onChange={(value) =>
              setMonthlyWithdrawal(
                snapToStep(value, INPUT_LIMITS.monthlyWithdrawal.step)
              )
            }
            min={INPUT_LIMITS.monthlyWithdrawal.min}
            max={INPUT_LIMITS.monthlyWithdrawal.max}
            step={INPUT_LIMITS.monthlyWithdrawal.step}
            prefix="₹"
            {...sliderProps}
          />

          <RangeSlider
            label="Yearly Increase in Withdrawal"
            value={yearlyWithdrawalIncrease}
            onChange={setYearlyWithdrawalIncrease}
            min={INPUT_LIMITS.yearlyWithdrawalIncrease.min}
            max={INPUT_LIMITS.yearlyWithdrawalIncrease.max}
            step={INPUT_LIMITS.yearlyWithdrawalIncrease.step}
            suffix="%"
            {...sliderProps}
          />

          <RangeSlider
            label="Expected Return During Withdrawal"
            value={returnDuringWithdrawal}
            onChange={setReturnDuringWithdrawal}
            min={INPUT_LIMITS.returnDuringWithdrawal.min}
            max={INPUT_LIMITS.returnDuringWithdrawal.max}
            step={INPUT_LIMITS.returnDuringWithdrawal.step}
            suffix="%"
            {...sliderProps}
          />
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            corpusBeforeSwp={result.corpusBeforeSwp}
            corpusUsed={result.corpusUsed}
            finalCorpus={result.finalCorpus}
            withdrawalStartAge={withdrawalStartAge}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
            <ChartContainer>
              <ResponsiveContainer width="100%" height={400} key={chartKey}>
                <BarChart
                  barCategoryGap="50%"
                  barGap={2}
                  barSize={5}
                  data={chartData}
                  margin={{top: 10, right: 5, left: 5, bottom: 30}}
                >
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    axisLine={false}
                    dataKey="label"
                    tick={{fontSize: 12, fill: "#9ca3af"}}
                    tickLine={false}
                  />
                  <YAxis
                    axisLine={false}
                    domain={[0, "dataMax"]}
                    tick={{fontSize: 12, fill: "#9ca3af"}}
                    tickCount={5}
                    tickFormatter={formatCompactCurrency}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<ChartHoverInfo />}
                    cursor={colors.chartCursor}
                  />
                  <Legend
                    align="center"
                    content={<ChartLabel />}
                    layout="horizontal"
                    verticalAlign="bottom"
                  />
                  <Bar
                    activeBar={{fill: colors.primary, opacity: 1}}
                    dataKey="Investment"
                    fill={colors.primary}
                    name="Investment"
                    radius={[0, 0, 2, 2]}
                    stackId="a"
                  />
                  <Bar
                    activeBar={{fill: SWP_ACCENT, opacity: 1, stroke: "#ffffff", strokeWidth: 1}}
                    dataKey="Gain"
                    fill={SWP_ACCENT}
                    name="Gain"
                    radius={[3, 3, 0, 0]}
                    stackId="a"
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <YearlyTable yearlyData={result.returnData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default SwpCalculator;
